import { useRef, useEffect, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import ConfettiExplosion, { triggerConfetti } from '../ui/ConfettiExplosion'

export default function ConclusionSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [showFinalConfetti, setShowFinalConfetti] = useState(false)

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        setShowFinalConfetti(true)
        // Multiple bursts
        triggerConfetti({ particleCount: 100, origin: { x: 0.3, y: 0.7 } })
        setTimeout(() => triggerConfetti({ particleCount: 100, origin: { x: 0.7, y: 0.7 } }), 300)
        setTimeout(() => triggerConfetti({ particleCount: 150, origin: { x: 0.5, y: 0.5 }, spread: 100 }), 600)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isInView])

  return (
    <motion.section
      ref={ref}
      id="conclusion"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-primary via-primary-pink/10 to-dark-primary">
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-primary-pink/20 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/3 w-96 h-96 rounded-full bg-accent-pink/20 blur-3xl"
          animate={{
            scale: [1.3, 1, 1.3],
            opacity: [0.5, 0.3, 0.5],
            x: [0, -50, 0],
            y: [0, 30, 0]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl">
        {/* Thank You Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-3 px-6 py-3 bg-primary-pink/20 backdrop-blur-sm rounded-full border border-primary-pink/30 mb-8"
        >
          <span className="text-2xl">🙏</span>
          <span className="text-lg font-medium text-primary-pink">Merci</span>
          <span className="text-2xl">💕</span>
        </motion.div>

        {/* Main Title */}
        <motion.h2
          className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="block text-white mb-2">Merci d'Avoir Partagé</span>
          <span className="gradient-text">Ce Moment</span>
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-light-pink-100/80 mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
        >
          Notre Wrapped 2025, pour toujours maintenant.
          <br />
          Cette histoire n'est que le début.
        </motion.p>

        {/* Final Message */}
        <motion.div
          className="glass-card pink-glow max-w-xl mx-auto mb-12"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.7, type: 'spring' }}
        >
          <p className="text-lg text-light-pink-100/90 italic">
            "Merci d'avoir partagé ce moment
            <br />
            Notre Wrapped 2025, pour toujours maintenant"
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1 }}
        >
          <motion.button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-8 py-4 bg-primary-pink text-dark-primary font-bold rounded-full hover:shadow-lg hover:shadow-primary-pink/30 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🔄 Revoir Notre Histoire
          </motion.button>

          <motion.button
            onClick={() => {
              triggerConfetti({ particleCount: 200, spread: 120 })
            }}
            className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border border-white/20 hover:bg-white/20 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🎉 Célébrer !
          </motion.button>
        </motion.div>

        {/* Signature */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1.5 }}
        >
          <p className="text-primary-pink/70 text-sm">
            Fait avec ❤️ pour toi
          </p>
          <p className="text-light-pink-100/50 text-xs mt-2">
            2025
          </p>
        </motion.div>

        {/* Hearts */}
        <motion.div
          className="mt-8 flex justify-center gap-2"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1.8 }}
        >
          {['❤️', '🧡', '💛', '💚', '💙', '💜', '🩷', '🤍'].map((heart, i) => (
            <motion.span
              key={i}
              className="text-2xl"
              animate={{
                y: [0, -10, 0],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.1
              }}
            >
              {heart}
            </motion.span>
          ))}
        </motion.div>
      </div>

      {/* Confetti */}
      {showFinalConfetti && <ConfettiExplosion trigger={true} particleCount={100} />}
    </motion.section>
  )
}
